import React, { useState } from 'react';
import { 
  FaRocket, FaUser, FaChartLine, FaFileAlt, FaCheckCircle,
  FaArrowRight, FaArrowLeft, FaTimes, FaHandshake, FaShieldAlt,
  FaBullseye, FaLightbulb, FaStar, FaGraduationCap
} from 'react-icons/fa';

const OnboardingWizard = ({ user, advisor, onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      id: 'welcome',
      title: 'Welcome to Arth Gurukul',
      subtitle: 'Your School for Lifelong Financial Wellness',
      icon: FaRocket,
      color: 'from-[#D4AF37] to-[#FFD700]',
      content: (
        <div className="text-center space-y-6">
          <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-r from-[#D4AF37] to-[#FFD700] flex items-center justify-center">
            <FaRocket className="text-[#0A1929] text-4xl" />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">
              Welcome, <span className="text-[#FFD700]">{user?.name || 'Client'}!</span>
            </h2>
            <p className="text-gray-300 text-lg">
              We're excited to help you on your journey to financial success.
            </p>
          </div>
          <div className="bg-[#0F172A] rounded-xl p-6 border border-[#2D5A8F] max-w-md mx-auto">
            <p className="text-gray-300">
              Arth Gurukul is a comprehensive financial planning tool that helps you:
            </p>
            <ul className="mt-4 space-y-2 text-left">
              <li className="flex items-center text-gray-300">
                <FaCheckCircle className="text-green-400 mr-3 flex-shrink-0" />
                Track your complete financial picture
              </li>
              <li className="flex items-center text-gray-300">
                <FaCheckCircle className="text-green-400 mr-3 flex-shrink-0" />
                Set and achieve your financial goals
              </li>
              <li className="flex items-center text-gray-300">
                <FaCheckCircle className="text-green-400 mr-3 flex-shrink-0" />
                Get personalized advice from your advisor
              </li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'advisor',
      title: 'Meet Your Financial Advisor',
      subtitle: 'Your trusted partner in financial planning',
      icon: FaHandshake,
      color: 'from-blue-500 to-blue-600',
      content: (
        <div className="text-center space-y-6">
          <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center">
            <FaHandshake className="text-white text-4xl" />
          </div>
          {advisor ? (
            <div className="bg-[#0F172A] rounded-xl p-6 border border-blue-500/50 max-w-md mx-auto">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#FFD700] flex items-center justify-center">
                <FaUser className="text-[#0A1929] text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-white">{advisor.name}</h3>
              {advisor.branding?.designation && (
                <p className="text-[#FFD700] font-medium">{advisor.branding.designation}</p>
              )}
              {advisor.branding?.company && (
                <p className="text-gray-400 text-sm mt-1">{advisor.branding.company}</p>
              )}
              <div className="mt-4 pt-4 border-t border-[#2D5A8F]">
                <p className="text-gray-300 text-sm">
                  Your advisor will help you create a personalized financial plan, 
                  analyze your insurance needs, and guide you towards your goals.
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-[#0F172A] rounded-xl p-6 border border-[#2D5A8F] max-w-md mx-auto">
              <p className="text-gray-300">
                A dedicated financial advisor will be assigned to you soon. 
                They will guide you through your financial planning journey.
              </p>
            </div>
          )}
          <div className="grid grid-cols-3 gap-4 max-w-md mx-auto">
            <div className="bg-[#1A2942] rounded-lg p-3 border border-[#2D5A8F]">
              <FaShieldAlt className="text-green-400 text-xl mx-auto mb-2" />
              <p className="text-gray-400 text-xs">Trusted Advice</p>
            </div>
            <div className="bg-[#1A2942] rounded-lg p-3 border border-[#2D5A8F]">
              <FaBullseye className="text-[#FFD700] text-xl mx-auto mb-2" />
              <p className="text-gray-400 text-xs">Goal Focused</p>
            </div>
            <div className="bg-[#1A2942] rounded-lg p-3 border border-[#2D5A8F]">
              <FaLightbulb className="text-blue-400 text-xl mx-auto mb-2" />
              <p className="text-gray-400 text-xs">Expert Insights</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'howItWorks',
      title: 'How It Works',
      subtitle: 'Simple steps to your financial plan',
      icon: FaChartLine,
      color: 'from-purple-500 to-purple-600',
      content: (
        <div className="space-y-6">
          <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-r from-purple-500 to-purple-600 flex items-center justify-center">
            <FaChartLine className="text-white text-4xl" />
          </div>
          <div className="max-w-lg mx-auto">
            <div className="space-y-4">
              {/* Step 1 */}
              <div className="flex items-start space-x-4 bg-[#0F172A] rounded-xl p-4 border border-[#2D5A8F]">
                <div className="w-10 h-10 rounded-full bg-[#FFD700] flex items-center justify-center flex-shrink-0">
                  <span className="text-[#0A1929] font-bold">1</span>
                </div>
                <div>
                  <h4 className="text-white font-bold">Fill Your Financial Details</h4>
                  <p className="text-gray-400 text-sm">
                    Complete your profile with income, assets, liabilities, insurance, and goals.
                  </p>
                </div>
              </div>
              
              {/* Step 2 */}
              <div className="flex items-start space-x-4 bg-[#0F172A] rounded-xl p-4 border border-[#2D5A8F]">
                <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">2</span>
                </div>
                <div>
                  <h4 className="text-white font-bold">Submit for Review</h4>
                  <p className="text-gray-400 text-sm">
                    Once complete, submit your details. Your advisor will review and analyze them.
                  </p>
                </div>
              </div>
              
              {/* Step 3 */}
              <div className="flex items-start space-x-4 bg-[#0F172A] rounded-xl p-4 border border-[#2D5A8F]">
                <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">3</span>
                </div>
                <div>
                  <h4 className="text-white font-bold">Get Your Financial Report</h4>
                  <p className="text-gray-400 text-sm">
                    Receive a comprehensive report with gap analysis, recommendations, and action items.
                  </p>
                </div>
              </div>
              
              {/* Step 4 */}
              <div className="flex items-start space-x-4 bg-[#0F172A] rounded-xl p-4 border border-[#2D5A8F]">
                <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">4</span>
                </div>
                <div>
                  <h4 className="text-white font-bold">Track & Achieve Goals</h4>
                  <p className="text-gray-400 text-sm">
                    Monitor your progress and work with your advisor to achieve your financial goals.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'features',
      title: 'Platform Features',
      subtitle: 'Everything you need for financial success',
      icon: FaStar,
      color: 'from-orange-500 to-orange-600',
      content: (
        <div className="space-y-6">
          <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-r from-orange-500 to-orange-600 flex items-center justify-center">
            <FaStar className="text-white text-4xl" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <div className="bg-[#0F172A] rounded-xl p-4 border border-[#2D5A8F] hover:border-[#FFD700] transition-colors">
              <div className="w-10 h-10 rounded-lg bg-[#FFD700]/20 flex items-center justify-center mb-3">
                <FaFileAlt className="text-[#FFD700]" />
              </div>
              <h4 className="text-white font-bold mb-1">Financial Reports</h4>
              <p className="text-gray-400 text-sm">Detailed analysis of your financial health with actionable insights.</p>
            </div>
            
            <div className="bg-[#0F172A] rounded-xl p-4 border border-[#2D5A8F] hover:border-blue-500 transition-colors">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center mb-3">
                <FaShieldAlt className="text-blue-400" />
              </div>
              <h4 className="text-white font-bold mb-1">Insurance Gap Analysis</h4>
              <p className="text-gray-400 text-sm">Know if you have adequate life and health insurance coverage.</p>
            </div>
            
            <div className="bg-[#0F172A] rounded-xl p-4 border border-[#2D5A8F] hover:border-green-500 transition-colors">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center mb-3">
                <FaBullseye className="text-green-400" />
              </div>
              <h4 className="text-white font-bold mb-1">Goal Planning</h4>
              <p className="text-gray-400 text-sm">Plan for retirement, children's education, marriage, and more.</p>
            </div>
            
            <div className="bg-[#0F172A] rounded-xl p-4 border border-[#2D5A8F] hover:border-purple-500 transition-colors">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center mb-3">
                <FaGraduationCap className="text-purple-400" />
              </div>
              <h4 className="text-white font-bold mb-1">Money School</h4>
              <p className="text-gray-400 text-sm">Access educational content to improve your financial literacy.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'getStarted',
      title: "Let's Get Started!",
      subtitle: 'Begin your financial planning journey',
      icon: FaCheckCircle,
      color: 'from-green-500 to-green-600',
      content: (
        <div className="text-center space-y-6">
          <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-r from-green-500 to-green-600 flex items-center justify-center">
            <FaCheckCircle className="text-white text-4xl" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">You're All Set!</h2>
            <p className="text-gray-300">
              Click the button below to start filling your financial details.
            </p>
          </div>
          
          <div className="bg-[#0F172A] rounded-xl p-6 border border-green-500/50 max-w-md mx-auto">
            <h4 className="text-white font-bold mb-4">Quick Tips:</h4>
            <ul className="space-y-3 text-left">
              <li className="flex items-start text-gray-300">
                <FaCheckCircle className="text-green-400 mr-3 mt-1 flex-shrink-0" />
                <span>Have your income documents handy (salary slips, ITR)</span>
              </li>
              <li className="flex items-start text-gray-300">
                <FaCheckCircle className="text-green-400 mr-3 mt-1 flex-shrink-0" />
                <span>Keep your investment statements ready (MF, FD, PPF)</span>
              </li>
              <li className="flex items-start text-gray-300">
                <FaCheckCircle className="text-green-400 mr-3 mt-1 flex-shrink-0" />
                <span>Note down your loan EMI details and outstanding amounts</span>
              </li>
              <li className="flex items-start text-gray-300">
                <FaCheckCircle className="text-green-400 mr-3 mt-1 flex-shrink-0" />
                <span>Gather your insurance policy documents</span>
              </li>
            </ul>
          </div>
          
          <p className="text-gray-400 text-sm">
            Don't worry if you don't have all details now. You can always save and continue later!
          </p>
        </div>
      )
    }
  ];

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = () => {
    if (isLastStep) {
      onComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#0A1929]/95 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="onboarding-wizard">
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-2xl border border-[#2D5A8F] max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="p-4 border-b border-[#2D5A8F] flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 rounded-lg bg-gradient-to-r ${currentStepData.color} flex items-center justify-center`}>
              <currentStepData.icon className="text-white" />
            </div>
            <div>
              <h3 className="text-white font-bold">{currentStepData.title}</h3>
              <p className="text-gray-400 text-sm">{currentStepData.subtitle}</p>
            </div>
          </div>
          <button
            onClick={onSkip}
            className="p-2 hover:bg-[#0F172A] rounded-lg text-gray-400 hover:text-white transition-colors"
            title="Skip onboarding"
            data-testid="skip-onboarding-btn"
          >
            <FaTimes size={18} />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 pt-4">
          <div className="flex items-center space-x-2">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                    index < currentStep
                      ? 'bg-green-500 text-white'
                      : index === currentStep
                      ? `bg-gradient-to-r ${currentStepData.color} text-white`
                      : 'bg-[#0F172A] text-gray-500 border border-[#2D5A8F]'
                  }`}
                >
                  {index < currentStep ? <FaCheckCircle size={14} /> : index + 1}
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-1 rounded ${
                      index < currentStep ? 'bg-green-500' : 'bg-[#2D5A8F]'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
          <p className="text-center text-gray-500 text-xs mt-2">
            Step {currentStep + 1} of {steps.length}
          </p>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[50vh]">
          {currentStepData.content}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[#2D5A8F] flex items-center justify-between">
          <button
            onClick={handlePrev}
            disabled={isFirstStep}
            className={`px-6 py-2.5 rounded-lg font-medium flex items-center space-x-2 transition-all ${
              isFirstStep
                ? 'bg-[#1E3A5F]/50 text-gray-500 cursor-not-allowed'
                : 'bg-[#1E3A5F] text-white hover:bg-[#2D5A8F] border border-[#2D5A8F]'
            }`}
            data-testid="prev-step-btn"
          >
            <FaArrowLeft size={14} />
            <span>Previous</span>
          </button>

          <button
            onClick={onSkip}
            className="text-gray-400 hover:text-white text-sm underline"
            data-testid="skip-link"
          >
            Skip for now
          </button>

          <button
            onClick={handleNext}
            className={`px-6 py-2.5 rounded-lg font-bold flex items-center space-x-2 transition-all ${
              isLastStep
                ? 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700'
                : 'bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] hover:from-[#FFD700] hover:to-[#D4AF37]'
            }`}
            data-testid="next-step-btn"
          >
            <span>{isLastStep ? "Start My Financial Journey" : 'Next'}</span>
            <FaArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
